/**
 * Where the visitor came from BEFORE callizy.ca, kept until the account exists.
 *
 * On 24 Oct 2026 a sign-up (company 280) reached the app with
 * `https://callizy.ca/` as referrer: useless. The marketing site reads the
 * real origin (external referrer domain, utm_*, Google Ads click id, landing
 * path) and adds it to its links to the app, with `first_seen`, the date of
 * its first contact. A visitor who lands here directly brings their own.
 *
 * Since 2 Nov 2026 the FIRST contact is kept 90 days in localStorage: a later
 * visit never replaces it, except an older first contact handed over by the
 * site. The last Google Ads click (gclid, or gbraid / wbraid on iPhone) is
 * kept apart and replaced by each new click.
 *
 * The site also leaves both in a cookie on .callizy.ca (callizy_provenance),
 * for a visitor who comes back later straight to the app, without a link:
 * taken over here like the link (reprendreTemoin), cleaned the same way.
 *
 * Sent once, with the email sign-up or with oauth-init (it survives the round
 * trip to Google), then forgotten. The server cleans everything again, writes
 * it when it creates the company (services/provenance.py) and gives it to
 * PostHog with signup_completed: nothing is sent to PostHog from here.
 */

const CLE = 'callizy_provenance'
const CLE_CLIC = 'callizy_dernier_clic'
const DUREE_MS = 90 * 24 * 3600 * 1000

export interface Provenance {
  referrer_domain?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  landing_page?: string
}

type Clics = Pick<Provenance, 'gclid' | 'gbraid' | 'wbraid'>

/** What is stored: the value and when it was first seen (ms). */
interface Garde<T> {
  at: number
  v: T
}

const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const CLICS = ['gclid', 'gbraid', 'wbraid'] as const

/** Hosts inside our own flows (sign-in, payment): never where someone came from. */
const INTERNES = /(^|\.)(callizy\.ca|stripe\.com)$|^accounts\.google\./

/** Storage throws in some private modes: never blocking. */
function sansEchec<T>(f: () => T): T | null {
  try {
    return f()
  } catch {
    return null
  }
}

/** One printable line, trimmed to `max`. URL params are user input like any other. */
function propre(v: string | null | undefined, max: number): string {
  const imprimable = Array.from(v || '').filter((c) => c >= ' ' && c !== '\u007f').join('')
  return imprimable.replace(/\s+/g, ' ').trim().slice(0, max)
}

/** A bare host without `www.`, or '' when it is one of ours or unreadable. */
function domaine(v: string | null | undefined): string {
  const d = propre(v, 253).toLowerCase().replace(/\.$/, '').replace(/^www\./, '')
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) || INTERNES.test(d)) return ''
  return d.slice(0, 100)
}

/** A path only: query strings and fragments can carry anything. */
function chemin(v: string | null | undefined): string {
  const p = propre(v, 1000).split(/[?#]/)[0]
  return p.startsWith('/') && !p.startsWith('//') ? p.slice(0, 200) : ''
}

function hoteDuReferent(): string {
  return sansEchec(() => (document.referrer ? new URL(document.referrer).hostname : '')) || ''
}

/** Google Ads click ids, kept as they are (Google needs them), only when well formed. */
function clicsDe(p: URLSearchParams): Clics {
  const v: Clics = {}
  for (const k of CLICS) {
    const x = (p.get(k) || '').trim()
    if (/^[A-Za-z0-9_.~-]{8,255}$/.test(x)) v[k] = x
  }
  return v
}

/** Ad platforms tag their clicks with an id instead of utm: the source it reveals. */
function sourceDePub(p: URLSearchParams): Pick<Provenance, 'utm_source' | 'utm_medium'> {
  if (p.has('gclid') || p.has('gbraid') || p.has('wbraid')) return { utm_source: 'google', utm_medium: 'cpc' }
  if (p.has('msclkid')) return { utm_source: 'bing', utm_medium: 'cpc' }
  if (p.has('fbclid')) return { utm_source: 'facebook' }
  return {}
}

/** A date (ms) within the last 90 days, or null. */
function dateValide(at: unknown): number | null {
  const maintenant = Date.now()
  return typeof at === 'number' && Number.isFinite(at) && maintenant - at <= DUREE_MS && at <= maintenant + 60_000
    ? at
    : null
}

/** A kept value younger than 90 days, or null (an expired or unreadable one is dropped). */
function lireGarde<T>(cle: string): Garde<T> | null {
  const brut = sansEchec(() => localStorage.getItem(cle))
  if (!brut) return null
  const g = sansEchec(() => JSON.parse(brut) as Garde<T>)
  if (!g || typeof g !== 'object' || dateValide(g.at) === null || !g.v || typeof g.v !== 'object') {
    sansEchec(() => localStorage.removeItem(cle))
    return null
  }
  return g
}

function garder<T>(cle: string, v: T, at = Date.now()): void {
  sansEchec(() => localStorage.setItem(cle, JSON.stringify({ at, v })))
}

/** The cookie the site writes on .callizy.ca (src/lib/provenance.ts on the site). */
const TEMOIN = 'callizy_provenance'

function attributsTemoin(): string {
  const hote = window.location.hostname
  const domaine = hote === 'callizy.ca' || hote.endsWith('.callizy.ca') ? '; Domain=.callizy.ca' : ''
  return `; Path=/${domaine}; SameSite=Lax`
}

/** String values only: whatever else a cookie holds is dropped. */
function enParams(brut: unknown): URLSearchParams {
  const p = new URLSearchParams()
  if (brut && typeof brut === 'object') {
    for (const [k, x] of Object.entries(brut as Record<string, unknown>)) if (typeof x === 'string') p.set(k, x)
  }
  return p
}

/** A first contact read from outside this page, cleaned like the URL. */
function provenanceDe(p: URLSearchParams): Provenance {
  const v: Provenance = {}
  for (const k of UTM) {
    const x = propre(p.get(k), 150)
    if (x) v[k] = x
  }
  Object.assign(v, clicsDe(p))
  const d = domaine(p.get('referrer_domain'))
  if (d) v.referrer_domain = d
  const page = chemin(p.get('landing_page'))
  if (page) v.landing_page = page
  return v
}

function gardeDuTemoin<T extends object>(g: unknown, lire: (p: URLSearchParams) => T): Garde<T> | null {
  if (!g || typeof g !== 'object') return null
  const { at, v } = g as { at?: unknown; v?: unknown }
  const quand = dateValide(at)
  if (quand === null) return null
  const valeur = lire(enParams(v))
  return Object.keys(valeur).length ? { at: quand, v: valeur } : null
}

/** Takes over the site's cookie: its first contact when older than ours, its click when newer. */
function reprendreTemoin(): void {
  const brut = sansEchec(() =>
    document.cookie.split('; ').find((x) => x.startsWith(`${TEMOIN}=`))?.slice(TEMOIN.length + 1),
  )
  if (!brut) return
  const t = sansEchec(() => JSON.parse(decodeURIComponent(brut)) as { p?: unknown; c?: unknown })
  if (!t || typeof t !== 'object') return

  const sien = gardeDuTemoin(t.p, provenanceDe)
  const premier = lireGarde<Provenance>(CLE)
  if (sien && (!premier || sien.at < premier.at)) garder(CLE, sien.v, sien.at)

  const sonClic = gardeDuTemoin(t.c, clicsDe)
  const clic = lireGarde<Clics>(CLE_CLIC)
  if (sonClic && (!clic || sonClic.at > clic.at)) garder(CLE_CLIC, sonClic.v, sonClic.at)
}

/**
 * Reads the page's URL, on the first page of the app. A Google Ads click
 * replaces the last one. The first contact is written when none is kept, or
 * when the site hands over an older one.
 */
export function capturer(): void {
  // The OAuth return and magic links come from Google or a mailbox, not from a visit.
  if (window.location.pathname.includes('/auth/')) return

  reprendreTemoin()

  const p = new URLSearchParams(window.location.search)
  const clics = clicsDe(p)
  if (Object.keys(clics).length) garder(CLE_CLIC, clics)

  // Handed over by callizy.ca: the site's first page and referrer, not ours
  // (ours would only say callizy.ca), and when the site first saw the visitor.
  const viaSite = p.has('landing_page') || p.has('referrer_domain')
  const vuLe = viaSite ? dateValide(Number(p.get('first_seen')) * 1000) : null
  const at = vuLe ?? Date.now()
  const actuel = lireGarde<Provenance>(CLE)
  if (actuel && !(viaSite && at < actuel.at)) return

  const v: Provenance = {}
  for (const k of UTM) {
    const x = propre(p.get(k), 150)
    if (x) v[k] = x
  }
  if (!v.utm_source) Object.assign(v, sourceDePub(p))
  Object.assign(v, clics)
  const d = domaine(viaSite ? p.get('referrer_domain') : hoteDuReferent())
  if (d) v.referrer_domain = d
  const page = chemin(viaSite ? p.get('landing_page') : window.location.pathname)
  if (page) v.landing_page = page

  garder(CLE, v, at)
}

/** The first contact with the last Google Ads click on top, or null. */
export function lire(): Provenance | null {
  const premier = lireGarde<Provenance>(CLE)
  const clic = lireGarde<Clics>(CLE_CLIC)
  if (!premier && !clic) return null
  return { ...(premier ? premier.v : {}), ...(clic ? clic.v : {}) }
}

/** Forgets it once sent, so a second sign-up in this browser does not reuse it. */
export function oublier(): void {
  sansEchec(() => localStorage.removeItem(CLE))
  sansEchec(() => localStorage.removeItem(CLE_CLIC))
  // Kept per tab before 2 Nov 2026.
  sansEchec(() => sessionStorage.removeItem(CLE))
  // Or the next visit to the app would take it back from the site's cookie.
  sansEchec(() => {
    document.cookie = `${TEMOIN}=; Max-Age=0${attributsTemoin()}`
  })
}

/** `&key=value` pairs for oauth-init, which keeps them server side until Google returns. */
export function enParametres(): string {
  const v = lire()
  if (!v) return ''
  const p = new URLSearchParams()
  for (const [k, x] of Object.entries(v)) if (typeof x === 'string' && x) p.set(k, x)
  const s = p.toString()
  return s ? `&${s}` : ''
}
